import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import i18n from '@/utils/i18n';
import { FaCog, FaExclamationTriangle } from 'react-icons/all';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import useDebounce from '../../hooks/useDebounce';
import { MerchantStorePage } from '../../views/MerchantComponents/MerchantStorePage';
import AddStoreOrphanButton from '../../views/MerchantComponents/AddStoreOrphanButton';
import LoadingSpinner from '../../components/LoadingSpinner';
import { ContentSection } from '../../components/Layout/ContentSection';
import { MerchantStorePageContent } from '../../views/MerchantComponents/MerchantStorePageContent';
import { useSearchAdminV2Query } from '@/graphql/queries/searchAdminV2.generated';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PartnerHierarchyResults } from './PartnerHierarchyResults';
import { useResolveStoreBrand } from './useResolveStoreBrand';

const SEARCH_RESULT_MAX = 200;

type RouteParams = {
  accountId?: string;
  eventId?: string;
  storeId?: string;
};

export default function PartnerSearchNew(props: {
  match: { params: RouteParams };
}) {
  const { accountId, eventId, storeId } = props.match.params;
  const location = useLocation();
  const history = useHistory();

  const titleKey = storeId
    ? 'PageTitles:store'
    : eventId
      ? 'PageTitles:event'
      : accountId
        ? 'PageTitles:eventAccount'
        : 'PageTitles:partnerSearch';
  useDocumentTitle(titleKey);

  // Persist search state in URL query params so it survives route changes
  // (each /partner/account|event|store route remounts this component).
  const searchParams = new URLSearchParams(location.search);
  const keyword = searchParams.get('q') ?? '';
  const includeRemoved = searchParams.get('deleted') === '1';
  const includeEvents = searchParams.get('events') !== '0';
  const includePartnerAccounts = searchParams.get('partners') !== '0';
  const includeStores = searchParams.get('stores') !== '0';

  const setKeyword = useCallback(
    (value: string) => {
      const params = new URLSearchParams(location.search);
      if (value) params.set('q', value);
      else params.delete('q');
      history.replace({
        pathname: location.pathname,
        search: params.toString(),
      });
    },
    [history, location.pathname, location.search],
  );

  const setIncludeRemoved = useCallback(
    (value: boolean) => {
      const params = new URLSearchParams(location.search);
      if (value) params.set('deleted', '1');
      else params.delete('deleted');
      history.replace({
        pathname: location.pathname,
        search: params.toString(),
      });
    },
    [history, location.pathname, location.search],
  );

  const setBoolParam = useCallback(
    (key: string, value: boolean) => {
      const params = new URLSearchParams(location.search);
      if (value) params.delete(key);
      else params.set(key, '0');
      history.replace({
        pathname: location.pathname,
        search: params.toString(),
      });
    },
    [history, location.pathname, location.search],
  );

  const debouncedKeyword = useDebounce(keyword, 150);

  const skipSearch = !debouncedKeyword || debouncedKeyword.length < 3;
  const { data, loading } = useSearchAdminV2Query({
    fetchPolicy: 'no-cache',
    variables: {
      query: debouncedKeyword,
      limit: SEARCH_RESULT_MAX,
      filter: {
        include_deleted: includeRemoved,
        include_events: includeEvents,
        include_partner_accounts: includePartnerAccounts,
        include_stores: includeStores,
      },
    },
    skip: skipSearch,
  });

  const nodes = useMemo(
    () => data?.searchAdminV2?.partner_accounts ?? [],
    [data],
  );
  const orphanedStores = useMemo(
    () => data?.searchAdminV2?.orphaned_stores ?? [],
    [data],
  );
  const orphanedEvents = useMemo(
    () => data?.searchAdminV2?.orphaned_events ?? [],
    [data],
  );
  const resultCount =
    nodes.length + orphanedStores.length + orphanedEvents.length;

  // Removed/disabled stores that matched the query but were filtered out
  // because the "show disabled/removed" toggle is off. When the toggle is on
  // they are already included in the results, so only surface this otherwise.
  const removedStoresFilteredCount =
    data?.searchAdminV2?.removed_stores_filtered_count ?? 0;
  const hasFilteredRemovedStores =
    !includeRemoved && removedStoresFilteredCount > 0;

  // When the user lands directly on /partner/store/:storeId (bookmark, legacy
  // link) we need to resolve the brand_id since MerchantStorePage requires it.
  const { brandId: resolvedBrandId, loading: loadingStoreBrand } =
    useResolveStoreBrand(storeId);

  return (
    <>
      <ContentSection title={i18n.t('Common:partnerSearch')} data-use-bs={true}>
        <div className="d-flex align-items-center flex-row">
          <div className="mb-2 me-auto" />
          <AddStoreOrphanButton />
        </div>

        <div className="input-group mb-2 mt-3">
          <div className="input-group-text">{i18n.t('Common:search')}</div>
          <input
            type="text"
            name="partner-search"
            className="form-control"
            autoComplete="off"
            placeholder={i18n.t('Common:storeAccountEventDebitor')}
            onChange={(e) => setKeyword(e.target.value)}
            value={keyword}
          />
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="secondary" size="default" type="button">
                <FaCog />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="tw:w-72">
              <PopoverHeader className="tw:font-medium">
                {i18n.t('PartnerSearch:searchOptions')}
              </PopoverHeader>
              <div className="tw:flex tw:flex-col tw:gap-2">
                <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
                  <Checkbox
                    id="include-removed"
                    checked={includeRemoved}
                    onCheckedChange={(checked) =>
                      setIncludeRemoved(checked === true)
                    }
                  />
                  <Label htmlFor="include-removed">
                    {i18n.t('PartnerSearch:showDisabledRemovedBrands')}
                  </Label>
                </div>
                <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
                  <Checkbox
                    id="include-partner-accounts"
                    checked={includePartnerAccounts}
                    onCheckedChange={(checked) =>
                      setBoolParam('partners', checked === true)
                    }
                  />
                  <Label htmlFor="include-partner-accounts">
                    {i18n.t('PartnerSearch:includePartnerAccounts')}
                  </Label>
                </div>
                <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
                  <Checkbox
                    id="include-stores"
                    checked={includeStores}
                    onCheckedChange={(checked) =>
                      setBoolParam('stores', checked === true)
                    }
                  />
                  <Label htmlFor="include-stores">
                    {i18n.t('PartnerSearch:includeStores')}
                  </Label>
                </div>
                <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
                  <Checkbox
                    id="include-events"
                    checked={includeEvents}
                    onCheckedChange={(checked) =>
                      setBoolParam('events', checked === true)
                    }
                  />
                  <Label htmlFor="include-events">
                    {i18n.t('PartnerSearch:includeEvents')}
                  </Label>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <ScrollArea className="tw:h-[400px] tw:rounded tw:border">
          {loading && (
            <div className="p-3">
              <LoadingSpinner />
            </div>
          )}
          {!loading && skipSearch && (
            <div className="ps-3 pt-2">
              {i18n.t('PartnerSearch:enterKeywordMinLength')}
            </div>
          )}
          {!loading &&
            !skipSearch &&
            resultCount === 0 &&
            (hasFilteredRemovedStores ? (
              <div className="p-3">
                <Alert variant="destructive">
                  <FaExclamationTriangle />
                  <AlertDescription>
                    {i18n.t('PartnerSearch:onlyDisabledEntriesFound', {
                      count: removedStoresFilteredCount,
                    })}
                  </AlertDescription>
                </Alert>
              </div>
            ) : (
              <div className="text-muted ps-3 pt-2">
                {i18n.t('Common:noResults')}
              </div>
            ))}
          {!loading && !skipSearch && resultCount > 0 && (
            <>
              {hasFilteredRemovedStores && (
                <div className="tw:flex tw:items-center tw:gap-1.5 tw:px-3 tw:py-1.5 tw:text-xs tw:text-amber-600">
                  <FaExclamationTriangle className="tw:shrink-0" />
                  <span>
                    {i18n.t('PartnerSearch:filteredOutDisabled', {
                      count: removedStoresFilteredCount,
                    })}
                  </span>
                </div>
              )}
              <PartnerHierarchyResults
                nodes={nodes}
                orphanedStores={orphanedStores}
                orphanedEvents={orphanedEvents}
                selectedStoreId={storeId}
              />
            </>
          )}
          {resultCount >= SEARCH_RESULT_MAX && (
            <div className="text-muted ms-2 mt-3">
              {i18n.t('PartnerSearch:showingFirstResults', {
                maxResults: SEARCH_RESULT_MAX,
                totalResults: resultCount,
              })}
            </div>
          )}
        </ScrollArea>
      </ContentSection>

      {storeId && resolvedBrandId && !loadingStoreBrand && (
        <>
          <MerchantStorePage storeId={storeId} brandId={resolvedBrandId} />
          <MerchantStorePageContent
            storeId={storeId}
            brandId={resolvedBrandId}
          />
        </>
      )}
      {storeId && loadingStoreBrand && (
        <div className="p-3">
          <LoadingSpinner />
        </div>
      )}
    </>
  );
}
